import { FC, useState, useEffect, useCallback, MouseEventHandler, FormEvent } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useIntl, defineMessages, FormattedMessage } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link';
import { Input } from 'dibs-elements/exports/Input';
import MagnifyingGlass from 'dibs-icons/exports/legacy/MagnifyingGlass';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { PhotosSearchBrowseFacetBlock } from './PhotosSearchBrowseFacetBlock';
import { PhotosSearchBrowseFacetAppliedFilterTags } from './PhotosSearchBrowseFacetAppliedFilterTags';
import { trackSearchRefine } from './helpers/photosSearchBrowseTracking';
import { isFacetSearch } from './helpers/isFacetSearch';
import { FACET_TYPES } from './constants';

import styles from './PhotosSearchBrowseFacetFilter.scss';

import { PhotosSearchBrowseFacetFilter_photos$key as PhotosSearchBrowseFacetFilterPhotos } from './__generated__/PhotosSearchBrowseFacetFilter_photos.graphql';
import type {
    PhotosSearchBrowseFacetFilter_viewer$key as Viewer,
    PhotosSearchBrowseFacetFilter_viewer$data,
} from './__generated__/PhotosSearchBrowseFacetFilter_viewer.graphql';
import { FacetSearchPayload, ModifyFacetHandler } from './types';

const messages = defineMessages({
    placeholder: {
        id: 'abt.PhotosSearchBrowseFacetFilter.placeholder',
        defaultMessage: 'Search within {results, plural, one {# result} other {# results}}',
    },
});

const photosFragment = graphql`
    fragment PhotosSearchBrowseFacetFilter_photos on PhotosSearchBrowseQueryConnection {
        totalResults
        facets {
            imageStyleFacet {
                ...PhotosSearchBrowseFacetBlock_facets
            }
            imageRoomTypeFacet {
                ...PhotosSearchBrowseFacetBlock_facets
            }
            designProjectTypeFacet {
                ...PhotosSearchBrowseFacetBlock_facets
            }
            imageCreatorFacets {
                ...PhotosSearchBrowseFacetBlock_facets
            }
        }
    }
`;
const viewerFragment = graphql`
    fragment PhotosSearchBrowseFacetFilter_viewer on Viewer {
        hiddenPhotoSearchBrowseFreetextSearch: featureFlag(
            feature: "hiddenPhotoSearchBrowseFreetextSearch"
        )
    }
`;

type PhotosSearchBrowseFacetFilterFormProps = {
    viewer: PhotosSearchBrowseFacetFilter_viewer$data | null;
    isModalContent?: boolean;
    totalResults: number;
    facetSearch: FacetSearchPayload;
    onSearch: ModifyFacetHandler;
    onFacetDelete: ModifyFacetHandler;
    onFacetsClear: MouseEventHandler<HTMLAnchorElement>;
    originalUrl?: string;
};

const PhotosSearchBrowseFacetFilterForm: FC<PhotosSearchBrowseFacetFilterFormProps> = ({
    viewer,
    isModalContent,
    totalResults,
    facetSearch,
    onSearch,
    onFacetDelete,
    onFacetsClear,
    originalUrl,
}) => {
    const searchTermProp = facetSearch?.searchTerm;
    const [searchTerm, setSearchTerm] = useState('');
    useEffect(() => {
        setSearchTerm(searchTermProp);
    }, [searchTermProp]);

    const intl = useIntl();
    const hasFiltersApplied = isFacetSearch(facetSearch);
    const onSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();
            trackSearchRefine();
            onSearch({ searchTerm });
        },
        [searchTerm, onSearch]
    );

    return (
        <form data-tn="photos-search-form" className={styles.searchForm} onSubmit={onSubmit}>
            {!isModalContent && (
                <>
                    <div className={styles.headerWrapper}>
                        <span className={styles.header}>
                            {hasFiltersApplied ? (
                                <FormattedMessage
                                    id="abt.PhotosSearchBrowseFacetFilter.appliedFilters"
                                    defaultMessage="Applied Filters"
                                />
                            ) : (
                                <FormattedMessage
                                    id="abt.PhotosSearchBrowseFacetFilter.filterResults"
                                    defaultMessage="Filter Results"
                                />
                            )}
                        </span>
                        {hasFiltersApplied && (
                            <span className={styles.totalResults}>
                                <FormattedMessage
                                    id="abt.PhotosSearchBrowseFacetFilter.numOfResults"
                                    defaultMessage="{totalResults, plural, one {# result} other {# results}}"
                                    values={{ totalResults }}
                                />
                            </span>
                        )}
                    </div>

                    <PhotosSearchBrowseFacetAppliedFilterTags
                        originalUrl={originalUrl}
                        facetSearch={facetSearch}
                        onDelete={onFacetDelete}
                    />
                </>
            )}

            {viewer?.hiddenPhotoSearchBrowseFreetextSearch ? null : (
                <div className={styles.searchFieldWrapper}>
                    <Input
                        dataTn="photos-refine-search-input"
                        size={isModalContent ? 'large' : 'small'}
                        placeholder={intl.formatMessage(messages.placeholder, {
                            results: totalResults,
                        })}
                        value={searchTerm}
                        onChange={event => {
                            setSearchTerm(event.target.value || '');
                        }}
                        rightDecorator={
                            <Link
                                underline="none"
                                onClick={onSubmit}
                                dataTn="photos-refine-search-button"
                                className={styles.searchButton}
                            >
                                <MagnifyingGlass className={styles.searchIcon} />
                            </Link>
                        }
                    />
                </div>
            )}

            {!isModalContent && hasFiltersApplied && (
                <Link href="/photos/" className={styles.clearAll} onClick={onFacetsClear}>
                    <FormattedMessage
                        id="abt.PhotosSearchBrowseFacetAppliedFilterTags.clearAll"
                        defaultMessage="Clear All"
                    />
                </Link>
            )}
        </form>
    );
};

type PhotosSearchBrowseFacetFilterProps = {
    isModalContent?: boolean;
    photos: PhotosSearchBrowseFacetFilterPhotos | null;
    viewer: Viewer | null;
    facetSearch: FacetSearchPayload;
    onFacetChange: ModifyFacetHandler;
    onFacetDelete: ModifyFacetHandler;
    onSearch: ModifyFacetHandler;
    onFacetsClear: MouseEventHandler<HTMLAnchorElement>;
    originalUrl?: string;
};

export const PhotosSearchBrowseFacetFilter: FC<PhotosSearchBrowseFacetFilterProps> = props => {
    const {
        originalUrl,
        isModalContent,
        photos: photosRef,
        viewer: viewerRef,
        facetSearch,
        onSearch,
        onFacetChange,
        onFacetDelete,
        onFacetsClear,
    } = props;
    const photos = useFragment(photosFragment, photosRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const totalResults = photos?.totalResults || 0;
    const facets = photos?.facets;

    const facetStyle = facetSearch?.style || [];
    const facetRoom = facetSearch?.room || [];
    const facetProject = facetSearch?.project || [];
    const facetCreator = facetSearch?.creator || [];

    const filterForm = (
        <PhotosSearchBrowseFacetFilterForm
            originalUrl={originalUrl}
            isModalContent={isModalContent}
            totalResults={totalResults}
            facetSearch={facetSearch}
            onSearch={onSearch}
            onFacetDelete={onFacetDelete}
            onFacetsClear={onFacetsClear}
            viewer={viewer}
        />
    );

    return (
        <>
            {!isModalContent && filterForm}

            <PhotosSearchBrowseFacetBlock
                label={
                    <FormattedMessage
                        id="abt.PhotosSearchBrowseFacetFilter.assets.style"
                        defaultMessage="Style"
                    />
                }
                originalUrl={originalUrl}
                facetSearch={facetSearch}
                facetType={FACET_TYPES.STYLE}
                facets={(facets?.imageStyleFacet || []).filter(filterNulls)}
                checkedFacets={facetStyle}
                onChange={onFacetChange}
            />
            <PhotosSearchBrowseFacetBlock
                label={
                    <FormattedMessage
                        id="abt.PhotosSearchBrowseFacetFilter.assets.room"
                        defaultMessage="Room"
                    />
                }
                originalUrl={originalUrl}
                facetSearch={facetSearch}
                facetType={FACET_TYPES.ROOM}
                facets={(facets?.imageRoomTypeFacet || []).filter(filterNulls)}
                checkedFacets={facetRoom}
                onChange={onFacetChange}
            />
            <PhotosSearchBrowseFacetBlock
                label={
                    <FormattedMessage
                        id="abt.PhotosSearchBrowseFacetFilter.assets.project"
                        defaultMessage="Project"
                    />
                }
                originalUrl={originalUrl}
                facetSearch={facetSearch}
                facetType={FACET_TYPES.PROJECT}
                facets={(facets?.designProjectTypeFacet || []).filter(filterNulls)}
                checkedFacets={facetProject}
                onChange={onFacetChange}
            />
            <PhotosSearchBrowseFacetBlock
                label={
                    <FormattedMessage
                        id="abt.PhotosSearchBrowseFacetFilter.assets.creator"
                        defaultMessage="Creator"
                    />
                }
                originalUrl={originalUrl}
                facetSearch={facetSearch}
                facetType={FACET_TYPES.CREATOR}
                facets={(facets?.imageCreatorFacets || []).filter(filterNulls)}
                checkedFacets={facetCreator}
                onChange={onFacetChange}
            />

            {isModalContent && filterForm}
        </>
    );
};
